import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { dutySchemeType } from "@/components/pages/duty/composables/dutyConfiguration";
import type Guid from "@/app/types/common/guid";
import { LocalDate } from "@js-joda/core";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

export const dutyMenuAustralian = {
  root: "navigation_duty_group_australian",
  dutyReturnList: "australian_duty_return_list",
  dutyReturnListLocation: (): Location => ({
    name: dutyMenuAustralian.dutyReturnList,
  }),
  editReturn: "australian_duty_edit_return",
  editReturnLocation: (id: Guid, isEditing: boolean): Location => ({
    name: dutyMenuAustralian.editReturn,
    params: {
      id: id.toString(),
      mode: isEditing ? "edit" : "view",
    },
  }),
  createReturn: "australian_duty_create_return",
  createReturnLocation: (): Location => ({
    name: dutyMenuAustralian.createReturn,
  }),
  audit: "australian_duty_audit_report",
  viewAudit: (fromDate: LocalDate, toDate: LocalDate): Location => ({
    name: dutyMenuAustralian.audit,
    params: {
      fromDateInclusive: fromDate.toString(),
      toDateInclusive: toDate.toString(),
    },
  }),
  liquorReturn: "australian_duty_liquor_return",
  viewLiquorReturn: (fromDate: LocalDate, toDate: LocalDate): Location => ({
    name: dutyMenuAustralian.liquorReturn,
    params: {
      fromDateInclusive: fromDate.toString(),
      toDateInclusive: toDate.toString(),
    },
  }),
  wetAudit: "australian_wet_audit_report",
  viewWetAudit: (fromDate: LocalDate, toDate: LocalDate): Location => ({
    name: dutyMenuAustralian.wetAudit,
    params: {
      fromDateInclusive: fromDate.toString(),
      toDateInclusive: toDate.toString(),
    },
  }),
  wetReturnList: "australian_wet_return_list",
  wetReturnListLocation: (): Location => ({
    name: dutyMenuAustralian.wetReturnList,
  }),
  editWetReturn: "australian_wet_edit_return",
  editWetReturnLocation: (id: Guid, isEditing: boolean): Location => ({
    name: dutyMenuAustralian.editWetReturn,
    params: {
      id: id.toString(),
      mode: isEditing ? "edit" : "view",
    },
  }),
  createWetReturn: "australian_wet_create_return",
  createWetReturnLocation: (): Location => ({
    name: dutyMenuAustralian.createWetReturn,
  }),
};

export const buildDutyMenuAustralianItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: dutyMenuAustralian.root,
    icon: "Assignment",
    visible: () =>
      dutySchemeType.value.isAustralia() &&
      hasPermissionsUsingUserStore([EnumUserPermission.menuDutyView]),
    subMenus: [
      MenuItem.fromOpts({
        name: dutyMenuAustralian.dutyReturnList,
        url: "/duty/australian/return/list",
        breadcrumbParentName: dutyMenuAustralian.root,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/ListAustralianDutyReturn.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.editReturn,
        url: "/duty/australian/return/:id/:mode",
        breadcrumbParentName: dutyMenuAustralian.dutyReturnList,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/EditAustralianDutyReturn.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
            editing:
              route.params.id &&
              route.params.mode.toLowerCase().trim() === "edit",
          };
        },
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.createReturn,
        url: "/duty/australian/return/create",
        breadcrumbParentName: dutyMenuAustralian.dutyReturnList,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/EditAustralianDutyReturn.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          editing: true,
        }),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.audit,
        url: "/duty/australian/return/audit",
        componentProps: (r) => ({
          fromDateInclusive:
            r.params.fromDateInclusive &&
            LocalDate.parse(r.params.fromDateInclusive),
          toDateInclusive:
            r.params.toDateInclusive &&
            LocalDate.parse(r.params.toDateInclusive),
        }),
        view: () =>
          import("@/components/pages/duty/australian/AustralianDutyAudit.vue"),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.liquorReturn,
        url: "/duty/australian/westernaustralialiquorreturn",
        componentProps: (r) => ({
          fromDateInclusive:
            r.params.fromDateInclusive &&
            LocalDate.parse(r.params.fromDateInclusive),
          toDateInclusive:
            r.params.toDateInclusive &&
            LocalDate.parse(r.params.toDateInclusive),
        }),
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/WesternAustraliaLiquorReturn.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.wetReturnList,
        url: "/duty/australian/wet/list",
        breadcrumbParentName: dutyMenuAustralian.root,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/ListAustralianWetReturn.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.wetAudit,
        url: "/duty/australian/wet/audit",
        componentProps: (r) => ({
          fromDateInclusive:
            r.params.fromDateInclusive &&
            LocalDate.parse(r.params.fromDateInclusive),
          toDateInclusive:
            r.params.toDateInclusive &&
            LocalDate.parse(r.params.toDateInclusive),
        }),
        view: () =>
          import("@/components/pages/duty/australian/AustralianWetAudit.vue"),
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.editWetReturn,
        url: "/duty/australian/wet/:id/:mode",
        breadcrumbParentName: dutyMenuAustralian.wetReturnList,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/EditAustralianWetReturn.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
            editing:
              route.params.id &&
              route.params.mode.toLowerCase().trim() === "edit",
          };
        },
      }),
      MenuItem.fromOpts({
        name: dutyMenuAustralian.createWetReturn,
        url: "/duty/australian/wet/create",
        breadcrumbParentName: dutyMenuAustralian.wetReturnList,
        view: () =>
          import(
            /* webpackChunkName: "australian-duty" */ "@/components/pages/duty/australian/EditAustralianWetReturn.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          editing: true,
        }),
      }),
    ],
  }),
];
